import { OctagonPause } from 'lucide-react'
import { useState } from 'react'

import { useActivateKillSwitch, useDeactivateKillSwitch, useKillSwitch } from '../api/control'
import { formatDateTime } from '../lib/format'
import { useWithStepUp } from '../lib/useWithStepUp'
import { Modal } from './Modal'
import { StepUpDialog } from './StepUpDialog'
import { useToast } from './Toast'

/** El freno de emergencia global (docs/plan_bot_trading.md §4.5).
 *
 *  Vive en la barra superior y no en una pantalla porque el momento en que
 *  hace falta es siempre "ahora, desde donde esté". Frenar pide sesión y
 *  nada más; soltar exige re-autenticación, y por eso el botón de reactivar
 *  vive en el cartel y no acá: son dos acciones con dos pesos distintos y no
 *  tienen que parecerse.
 */
export function KillSwitchButton() {
  const { data: state } = useKillSwitch()
  const activate = useActivateKillSwitch()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const toast = useToast()

  if (state?.active) return null

  async function handleActivate() {
    try {
      await activate.mutateAsync(reason)
      setOpen(false)
      setReason('')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo frenar')
    }
  }

  return (
    <>
      <button
        className="kill-switch-button"
        onClick={() => setOpen(true)}
        title="Frenar todo — el próximo ciclo de cada bot se bloquea"
        aria-label="Frenar todo"
      >
        <OctagonPause size={20} />
      </button>

      {open && (
        <Modal onClose={() => setOpen(false)}>
          <h3>Frenar todos los bots</h3>
          <p className="muted">
            Ningún bot va a operar hasta que se desactive. No pausa los bots ni cierra posiciones
            abiertas: es un freno, y se levanta cuando quieras.
          </p>
          <div className="field">
            <label htmlFor="kill-switch-reason">Motivo (opcional)</label>
            <input
              id="kill-switch-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Qué está pasando"
              maxLength={500}
            />
          </div>
          <div className="confirm-dialog__actions">
            <button className="button button--ghost" onClick={() => setOpen(false)}>
              Cancelar
            </button>
            <button
              className="button button--danger"
              onClick={handleActivate}
              disabled={activate.isPending}
              autoFocus
            >
              {activate.isPending ? 'Frenando…' : 'Frenar todo'}
            </button>
          </div>
        </Modal>
      )}
    </>
  )
}

/** El cartel de que todo está frenado. Se muestra en todas las pantallas: el
 *  estado "ningún bot está operando" no puede depender de en qué pantalla
 *  esté parado el que mira. */
export function KillSwitchBanner() {
  const { data: state } = useKillSwitch()
  const deactivate = useDeactivateKillSwitch()
  const stepUp = useWithStepUp()
  const toast = useToast()

  if (!state?.active) return null

  async function handleDeactivate() {
    try {
      await stepUp.run(async () => {
        await deactivate.mutateAsync('')
      })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo desactivar')
    }
  }

  return (
    <>
      <div className="kill-banner" role="alert">
        <div>
          <strong>Kill switch activado: ningún bot está operando.</strong>{' '}
          {state.reason && <span>{state.reason}</span>}
          <span className="muted"> · desde {formatDateTime(state.updated_at)}</span>
        </div>
        <button
          className="button button--ghost"
          onClick={handleDeactivate}
          disabled={deactivate.isPending}
        >
          {deactivate.isPending ? 'Reactivando…' : 'Reactivar los bots'}
        </button>
      </div>

      {stepUp.promptOpen && (
        <StepUpDialog
          onCancel={stepUp.cancel}
          onConfirm={stepUp.confirm}
          isPending={stepUp.isConfirming}
          error={stepUp.confirmError}
        />
      )}
    </>
  )
}
